/**
 * MSAL configuration — one shared PublicClientApplication for the whole
 * component. Identity and role both derive from the Entra ID id token.
 *
 * NOTE: clientId / tenant are carried over from the previous monorepo build and
 * must be confirmed against the current app registration, which must also list
 * the runtime origin (http://localhost:3000 during `npm run dev`, plus the
 * production host) as a redirect URI of type SPA — otherwise Entra rejects the
 * sign-in.
 */
import {
  PublicClientApplication,
  EventType,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import {readEnv} from "../env";

const CLIENT_ID =  readEnv('VITE_AZURE_CLIENT_ID') ?? '';
const TENANT_ID = readEnv('VITE_AZURE_TENANT_ID') ?? '';

const configuration: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    // Bound to the current origin so the same build works on both the localhost
    // dev server and the production host without a hardcoded redirect.
    redirectUri: window.location.origin,
  },
  cache: {
    // sessionStorage keeps the token scoped to the tab/session, matching the
    // app's session-oriented lifecycle.
    cacheLocation: 'sessionStorage',
  },
};

export const msalInstance = new PublicClientApplication(configuration);

// Keep the active account in sync after an interactive login so subsequent
// getActiveAccount() calls resolve without re-prompting.
msalInstance.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS &&
    event.payload &&
    'account' in event.payload
  ) {
    const { account } = event.payload as { account: AccountInfo };
    msalInstance.setActiveAccount(account);
  }
});

/** Scopes requested at sign-in. User.Read is enough to identify the user. */
export const LOGIN_REQUEST = { scopes: ['User.Read'] };
